import { AfterViewInit, Component, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";

import { config } from "@config";
import { Observable, Subject } from "rxjs";
import { WalletEditComponent } from "../wallet-edit.component";
import { AssetInfoService } from "@main-services/split-data/asset-info.service";
import { CryptocoinService } from "@main-services/currency";
import { SegmentService } from "@main-services/segment.service";
import { CryptoWalletService, WalletViewHelper } from "@main-services/wallet/crypto_wallet.service";
import { Address, Cryptocoin, CryptoWallet } from "@models";
import { InfoService } from "@core-services/info.service";
import { ConfirmModalService } from "@core-services/confirm-modal.service";
import { take, tap } from "rxjs/operators";
import { WalletDetailsTrackingService } from "@main-services/wallet/wallet-details-tracking.service";
import { TranslationService } from "@core-services/translation.service";
import { CryptoDepositFacade } from "@modules/+new/payments/features/crypto-deposit/domain/+state/crypto-deposit.facade";
import { CryptoDepositStateService } from "@modules/+new/payments/features/crypto-deposit/domain/+state/crypto-deposit-state.service";
import { RudderstackService } from "@main-services/rudderstack.service";

@Component({
	templateUrl: "./cryptowallet-details.component.html",
	styleUrls: ["./cryptowallet-details.component.scss"],
	providers: [WalletDetailsTrackingService, CryptoDepositFacade, CryptoDepositStateService],
	standalone: false
})
export class CryptowalletDetailsComponent
	extends WalletEditComponent<Cryptocoin>
	implements OnInit, AfterViewInit, OnDestroy
{
	public showHistorical: boolean = false;
	public block: boolean = false;
	public showLoadingNewAddress: boolean = false;
	private currentAddress: Address = new Address();
	public defaultWallet: CryptoWallet = null;
	public coin: Cryptocoin = null;
	public _walletData: Subject<WalletViewHelper> = new Subject();
	public currentNetwork: string;
	public cryptocoinIcon: string;
	public networkIcon: string;
	public warning: string;
	private readonly flowName: string = "Deposit Asset";
	private readonly pathPrepend: string = "/deposit/asset";

	public constructor(
		protected tradableWalletService: CryptoWalletService,
		protected tradableService: CryptocoinService,
		private router: Router,
		private infoService: InfoService,
		private segmentService: SegmentService,
		private rudderstackService: RudderstackService,
		private assetInfoService: AssetInfoService,
		protected confirmModalService: ConfirmModalService,
		private walletDetailsTrackingService: WalletDetailsTrackingService,
		protected translationService: TranslationService,
		protected cryptoDepositFacade: CryptoDepositFacade
	) {
		super();
	}

	public get lang(): string {
		return this.translationService.getLanguage();
	}

	public get walletData(): Observable<WalletViewHelper> {
		return this._walletData;
	}

	public ngOnInit(): void {
		this.coin = this.tradableService.getItemById(this.data.wallet.getCurrencyId());
		this.assetInfoService.fetchApiInfo(this.coin);
		this.cryptocoinIcon = this.data.cryptocoinIcon;
		this.networkIcon = this.data.networkIcon;
		this.currentNetwork = this.data.networkName;
		this.warning = this.translationService.translate("wallet_address_send_coins_warning", [this.coin.getSymbol()]);
	}

	public ngAfterViewInit(): void {
		super.ngAfterViewInit();
		this.segmentService.track({
			name: this.flowName,
			properties: {
				category: "Trading interaction",
				label: "started"
			}
		});
		this.rudderstackService.track({
			name: this.flowName,
			properties: {
				category: "Trading interaction",
				label: "started"
			}
		});
	}

	public changeNetwork(): void {
		this.walletDetailsTrackingService.trackChangeNetworkClicked();
		this.cryptoDepositFacade.availableCryptoList$.pipe(take(1)).subscribe((list) => {
			const currentCrypto = list.find(
				(item) => item.symbol.toUpperCase() === this.getTradableSymbol().toUpperCase()
			);
			if (currentCrypto.networks.length > 1) {
				this.close();
				this.cryptoDepositFacade.navigateToNetworkSelection(currentCrypto.symbol);
			} else {
				this.infoService.setWarning(
					this.translationService.translate("only_one_address_available_for_deposit", [
						currentCrypto.symbol.toUpperCase()
					])
				);
			}
		});
	}

	public close(): void {
		this.walletDetailsTrackingService.trackCloseWalletAddress();
		super.close();
	}

	protected callObservableAfterView(): void {
		this.getWalletObservable().subscribe({
			next: (wallet: CryptoWallet) => this.setWalletData(wallet),
			error: (error) => {
				this.router.navigateByUrl("/portfolio");
				this.infoService.setError(error);
			}
		});
		if (!this.data.wallet.isDefault()) {
			this.defaultWallet = this.tradableWalletService.list.find(
				(w) => w.isDefault() && w.getCurrencyId() === this.data.wallet.getCurrencyId()
			);
		}
	}

	public openWalletDetails(): void {
		this.close();
		setTimeout(() => {
			this.router.navigateByUrl("/wallet/" + this.defaultWallet.getId());
			this.modalService
				.create(CryptowalletDetailsComponent, {
					data: { wallet: this.defaultWallet, cryptocoinIcon: this.cryptocoinIcon }
				})
				.present();
		}, 100);
	}

	public getCanCreateMore(): boolean {
		return this.tradableWalletService.getCanCreateMore();
	}

	public getCannotReason(): string {
		return this.tradableWalletService.getCannotCreateReason();
	}

	private callNewAddress(wallet: CryptoWallet): void {
		this.block = true;
		this.showLoadingNewAddress = true;
		if (wallet.isNew()) {
			this.tradableWalletService
				.getAddressesForWallet(wallet)
				.pipe(
					tap(() => {
						this.block = false;
						this.showLoadingNewAddress = false;
					})
				)
				.subscribe((wallet) => this.setWalletData(wallet));
			return;
		}
		this.tradableWalletService
			.createNewAddress(wallet)
			.pipe(take(1))
			.subscribe({
				next: (ans) => {
					this.block = false;
					this.showLoadingNewAddress = false;
					this._walletData.next(ans);
					this.callObservableAfterView();
				},
				error: (error) => {
					this.block = false;
					this.showLoadingNewAddress = false;
					this.infoService.setError(error);
				}
			});
	}

	public isMaxWalletNumberReached(crypto: string): boolean {
		return this.isReasonForNoNewAddress(crypto) && !this.getCanCreateMore();
	}

	public isReasonForNoNewAddress(useCase: string): boolean {
		const reason = this.tradableWalletService.getCannotCreateReason();
		if (useCase === "erc20") {
			return reason.includes(useCase) || reason.includes("creation");
		}
		return reason.includes(useCase);
	}

	public createNewAddress(wallet: CryptoWallet): void {
		if (this.block) {
			return;
		}
		const symbol = this.coin.getSymbol(true);
		if (config.modalCoins.includes(symbol)) {
			this.confirmModalService
				.showConfirmModal("modal_address_warning_header", this.coin.getExtraInfo(), "ok", "cancel")
				.pipe(take(1))
				.subscribe((accept) => {
					if (accept) {
						this.callNewAddress(wallet);
					}
				});
		} else {
			this.callNewAddress(wallet);
		}
	}

	public canCreateMoreAddress(): boolean {
		return this.coin.isDepositActive() && this.getCanCreateMore();
	}

	public showOtherWalletAddresses(data: WalletViewHelper): boolean {
		return data.otherAddresses.length > 0 && !this.coin.isIota();
	}

	public getAddressInfo(): string {
		if (this.isReasonForNoNewAddress("last")) {
			return "external_deposit_missing";
		}
		if (this.isReasonForNoNewAddress("miota")) {
			return "maximum_miota_addresses_reached";
		}
		return "";
	}

	protected setWalletData(wallet: CryptoWallet): void {
		if (wallet == null && (!this.data.wallet || this.data.wallet._id.length <= 0)) {
			this.router.navigateByUrl("/portfolio");
			this.close();
			return;
		}
		if (wallet == null && this.data.wallet._id.length > 0) {
			this.router.navigateByUrl("/wallet/" + this.data.wallet._id);
			this.close();
			return;
		}
		wallet.setAddresses(
			wallet
				.getAddresses()
				.filter((w) => !w.isDisabled())
				.sort((a, b) => b.getLastUpdated().getUnix() - a.getLastUpdated().getUnix())
		);

		const addrs = wallet.getAddresses().filter((a) => !a.isDisabled());

		const currAddr = addrs.length > 0 ? addrs[addrs.length - 1] : new Address();

		this._walletData.next({
			wallet,
			currentAddress: currAddr,
			otherAddresses: wallet.getAddresses().slice(0, addrs.length - 1)
		});

		this.currentAddress = currAddr;
		this.data.wallet = wallet;
		const currencyLabel = this.tradableService.getItemById(wallet.getCurrencyId()).getSymbol(true);

		this.segmentService.page({
			name:
				this.currentAddress.getAddress().length === 0 ? `${this.flowName} Address` : `${this.flowName} Details`,
			overridePath:
				`${this.pathPrepend}/` +
				(this.currentAddress.getAddress().length === 0 ? "addresscreation" : "addressdetails"),
			properties: { asset_category: currencyLabel }
		});
		this.rudderstackService.page({
			name:
				this.currentAddress.getAddress().length === 0 ? `${this.flowName} Address` : `${this.flowName} Details`,
			overridePath:
				`${this.pathPrepend}/` +
				(this.currentAddress.getAddress().length === 0 ? "addresscreation" : "addressdetails"),
			properties: { asset_category: currencyLabel }
		});

		if (this.currentAddress.getAddress().length !== 0) {
			this.segmentService.track({
				name: this.flowName,
				properties: {
					category: "Trading interaction",
					label: "successful"
				}
			});
			this.rudderstackService.track({
				name: this.flowName,
				properties: {
					category: "Trading interaction",
					label: "successful"
				}
			});
		}
	}

	public ngOnDestroy(): void {
		this.segmentService.track({
			name: this.flowName,
			properties: {
				category: "Trading interaction",
				label: "cancelled"
			}
		});
		this.rudderstackService.track({
			name: this.flowName,
			properties: {
				category: "Trading interaction",
				label: "cancelled"
			}
		});
		this.tradableWalletService.resetReason();
	}
}
